import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useBackgroundMusic } from '../contexts/BackgroundMusicContext'
import { useUserPreferences } from '../contexts/UserPreferencesContext'
import { ModuleName, SfxTrack } from '../enums/enums'
import { getSfxUrl } from '../utils/audio'
import { cueTextAtTime, parseWebVtt, VttCue } from '../utils/vtt'

/**
 * Milliseconds between the GO sound and the wrong-answer sound (DMVP-1035). The
 * ticket leaves the choice between immediate and a short silence open, so it is a
 * constant rather than a literal. Zero means the wrong-answer sound starts as soon
 * as the click is handled; the two overlap, which the audio pool supports.
 */
export const WRONG_ANSWER_DELAY_MS = 0

/** HTMLAudioElement volume (0–1). 1 is the browser maximum, same as MCQ SFX. */
export const DAX_EXPLANATION_VOLUME = 1

/**
 * Extra gain on Dax VO. The placeholders are quieter than the SFX wavs; HTML volume cannot
 * go above 1, so this Web Audio gain is the boost (3 ≈ +9.5 dB).
 */
export const DAX_EXPLANATION_GAIN = 3

export interface McqSoundFx {
  playSubmit: () => void
  playReset: () => void
  playOptionSelect: () => void
  playWrongAnswer: () => void
  playRightAnswer: () => void
  /** Dax explanation VO on a miss (why the pick is wrong, then why the right answers are right). Mutes BGM until the clips finish. */
  playExplanation: (urls: string[], subtitleUrls?: string[]) => void
  /** Stops Dax VO and restores BGM (Continue, unmount). */
  stopExplanation: () => void
  /** Active VTT cue for the clip that is playing now; empty between cues and when idle. */
  explanationCaption: string
  /** True while Dax explanation VO is playing. Continue stays locked until this flips false. */
  isExplanationPlaying: boolean
}

/**
 * The six MCQ sounds, for both the standard (Unity) and accessible views.
 *
 * Muting is handled one level down by playSoundFx, which respects the user's
 * background-music preference.
 *
 * @param levelName - the active module, e.g. ModuleName.Response. Picks the
 *   option-select sound: Response gets its own (DMVP-1034), everything else gets
 *   the default (DMVP-1033).
 */
export const useMcqSoundFx = (levelName: string): McqSoundFx => {
  const bgMusic = useBackgroundMusic()
  const userPreferences = useUserPreferences()
  const [explanationCaption, setExplanationCaption] = useState('')
  const [isExplanationPlaying, setIsExplanationPlaying] = useState(false)

  const urls = useMemo(() => ({
    submit: getSfxUrl(SfxTrack.McqSubmit),
    reset: getSfxUrl(SfxTrack.McqReset),
    optionSelect: getSfxUrl(
      levelName === ModuleName.Response ? SfxTrack.McqOptionSelectResponse : SfxTrack.McqOptionSelect
    ),
    wrongAnswer: getSfxUrl(SfxTrack.McqWrongAnswer),
    rightAnswer: getSfxUrl(SfxTrack.QuestionCorrect)
  }), [levelName])

  // Cleared on unmount so a pending wrong-answer sound cannot fire after the game
  // has gone away.
  const wrongAnswerTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const explanationAudioRef = useRef<HTMLAudioElement | null>(null)
  const explanationUrlsRef = useRef<string[]>([])
  const subtitleUrlsRef = useRef<string[]>([])
  const explanationIndexRef = useRef(0)
  const voiceoverMuteHeldRef = useRef(false)
  const playNextExplanationRef = useRef<() => void>(() => {})
  const bgMusicRef = useRef(bgMusic)
  bgMusicRef.current = bgMusic
  const audioContextRef = useRef<AudioContext | null>(null)
  const cuesRef = useRef<VttCue[]>([])
  const captionLoadGenRef = useRef(0)

  const releaseVoiceoverMute = useCallback((): void => {
    if (!voiceoverMuteHeldRef.current) return
    voiceoverMuteHeldRef.current = false
    bgMusicRef.current.setVoiceoverMute(false)
  }, [])

  const clearCaption = useCallback((): void => {
    captionLoadGenRef.current += 1
    cuesRef.current = []
    setExplanationCaption('')
  }, [])

  const stopVoOnly = useCallback((): void => {
    explanationUrlsRef.current = []
    subtitleUrlsRef.current = []
    explanationIndexRef.current = 0
    clearCaption()
    setIsExplanationPlaying(false)
    const audio = explanationAudioRef.current
    if (audio == null) return
    audio.pause()
    audio.removeAttribute('src')
    audio.load()
  }, [clearCaption])

  const loadCuesForIndex = useCallback((index: number): void => {
    const gen = ++captionLoadGenRef.current
    cuesRef.current = []
    setExplanationCaption('')
    const url = subtitleUrlsRef.current[index]
    if (url == null || url === '') return
    void fetch(url).then(async (response) => {
      if (!response.ok) return
      const text = await response.text()
      if (gen !== captionLoadGenRef.current) return
      cuesRef.current = parseWebVtt(text)
      const audio = explanationAudioRef.current
      if (audio != null) {
        setExplanationCaption(cueTextAtTime(cuesRef.current, audio.currentTime))
      }
    }).catch(() => {
      if (gen !== captionLoadGenRef.current) return
      cuesRef.current = []
    })
  }, [])

  const playNextExplanation = useCallback((): void => {
    const audio = explanationAudioRef.current
    if (audio == null) {
      setIsExplanationPlaying(false)
      return
    }
    if (explanationIndexRef.current >= explanationUrlsRef.current.length) {
      setIsExplanationPlaying(false)
      clearCaption()
      releaseVoiceoverMute()
      return
    }
    const index = explanationIndexRef.current
    audio.volume = DAX_EXPLANATION_VOLUME
    audio.src = explanationUrlsRef.current[index]
    explanationIndexRef.current += 1
    loadCuesForIndex(index)
    void audioContextRef.current?.resume()
    void audio.play().catch(() => {
      setIsExplanationPlaying(false)
      clearCaption()
      releaseVoiceoverMute()
    })
  }, [clearCaption, loadCuesForIndex, releaseVoiceoverMute])
  playNextExplanationRef.current = playNextExplanation

  useEffect(() => {
    return () => {
      if (wrongAnswerTimer.current !== null) clearTimeout(wrongAnswerTimer.current)
    }
  }, [])

  // Pre-create the VO element (same pattern as the SFX pool) so play() can run in the GO handler.
  useEffect(() => {
    const audio = new Audio()
    audio.volume = DAX_EXPLANATION_VOLUME
    explanationAudioRef.current = audio
    const AudioContextCtor = window.AudioContext
    if (typeof AudioContextCtor === 'function') {
      const ctx = new AudioContextCtor()
      const source = ctx.createMediaElementSource(audio)
      const gain = ctx.createGain()
      gain.gain.value = DAX_EXPLANATION_GAIN
      source.connect(gain)
      gain.connect(ctx.destination)
      audioContextRef.current = ctx
    }
    const onEnded = (): void => { playNextExplanationRef.current() }
    const onTimeUpdate = (): void => {
      const el = explanationAudioRef.current
      if (el == null) return
      setExplanationCaption(cueTextAtTime(cuesRef.current, el.currentTime))
    }
    audio.addEventListener('ended', onEnded)
    audio.addEventListener('timeupdate', onTimeUpdate)
    return () => {
      captionLoadGenRef.current += 1
      audio.removeEventListener('ended', onEnded)
      audio.removeEventListener('timeupdate', onTimeUpdate)
      audio.pause()
      explanationAudioRef.current = null
      void audioContextRef.current?.close()
      audioContextRef.current = null
      if (voiceoverMuteHeldRef.current) {
        voiceoverMuteHeldRef.current = false
        bgMusicRef.current.setVoiceoverMute(false)
      }
    }
  }, [])

  const play = useCallback((url: string): void => {
    bgMusic.playSoundFx(url)
  }, [bgMusic])

  const playSubmit = useCallback((): void => play(urls.submit), [play, urls.submit])
  const playReset = useCallback((): void => play(urls.reset), [play, urls.reset])
  const playOptionSelect = useCallback((): void => play(urls.optionSelect), [play, urls.optionSelect])

  const playWrongAnswer = useCallback((): void => {
    if (WRONG_ANSWER_DELAY_MS <= 0) {
      bgMusic.playSoundFx(urls.wrongAnswer)
      return
    }
    if (wrongAnswerTimer.current !== null) clearTimeout(wrongAnswerTimer.current)
    wrongAnswerTimer.current = setTimeout(() => {
      wrongAnswerTimer.current = null
      bgMusic.playSoundFx(urls.wrongAnswer)
    }, WRONG_ANSWER_DELAY_MS)
  }, [bgMusic, urls.wrongAnswer])

  // Unlike the wrong-answer sound (DMVP-1035), the ticket for this one (DMVP-1103) does not leave the
  // timing open - it always plays immediately after GO - so there is no equivalent delay constant here.
  const playRightAnswer = useCallback((): void => play(urls.rightAnswer), [play, urls.rightAnswer])

  const stopExplanation = useCallback((): void => {
    stopVoOnly()
    releaseVoiceoverMute()
  }, [releaseVoiceoverMute, stopVoOnly])

  const playExplanation = useCallback((explanationUrls: string[], subtitleUrls: string[] = []): void => {
    stopVoOnly()
    if (
      explanationUrls.length === 0 ||
      !bgMusic.isEnabled ||
      !userPreferences.isPreferencesLoaded ||
      !userPreferences.preferenceA11yBgm
    ) {
      releaseVoiceoverMute()
      return
    }
    voiceoverMuteHeldRef.current = true
    bgMusic.setVoiceoverMute(true)
    explanationUrlsRef.current = explanationUrls
    subtitleUrlsRef.current = subtitleUrls
    explanationIndexRef.current = 0
    setIsExplanationPlaying(true)
    playNextExplanation()
  }, [bgMusic, playNextExplanation, releaseVoiceoverMute, stopVoOnly, userPreferences.isPreferencesLoaded, userPreferences.preferenceA11yBgm])

  return { playSubmit, playReset, playOptionSelect, playWrongAnswer, playRightAnswer, playExplanation, stopExplanation, explanationCaption, isExplanationPlaying }
}
